<template>
  <div class="login-page">
    <main class="form-panel">
      <div class="form-wrap">
        <div class="mobile-brand" aria-label="启明服务管理平台">
          <p>QIMING CARE</p>
          <span>启明服务管理平台</span>
        </div>

        <div class="form-heading">
          <p class="form-heading__eyebrow">WELCOME BACK</p>
          <h2>登录账号</h2>
          <p>使用您的管理账号继续工作</p>
        </div>

        <form class="login-form" novalidate @submit.prevent="handleSubmit">
          <div class="field-group">
            <label for="account">账号</label>
            <input
              id="account"
              v-model.trim="account"
              type="text"
              name="account"
              autocomplete="username"
              placeholder="请输入账号"
              :class="{ 'has-error': errors.account }"
              @input="clearFieldError('account')"
            >
            <p v-if="errors.account" class="field-error">{{ errors.account }}</p>
          </div>

          <div class="field-group">
            <div class="field-label-row">
              <label for="password">密码</label>
              <button type="button" class="text-button" @click="togglePassword">
                {{ showPassword ? '隐藏密码' : '显示密码' }}
              </button>
            </div>
            <div class="password-input">
              <input
                id="password"
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                name="password"
                autocomplete="current-password"
                placeholder="请输入密码"
                :class="{ 'has-error': errors.password }"
                @input="clearFieldError('password')"
              >
            </div>
            <p v-if="errors.password" class="field-error">{{ errors.password }}</p>
          </div>

          <div class="form-options">
            <label class="remember-option">
              <input v-model="remember" type="checkbox" name="remember">
              <span class="checkbox" aria-hidden="true"></span>
              <span>记住登录状态</span>
            </label>
            <button type="button" class="text-button" @click="handleForgotPassword">
              忘记密码？
            </button>
          </div>

          <button class="submit-button" type="submit" :disabled="submitting">
            <span>{{ submitting ? '正在验证...' : submitted ? '登录成功' : '登录' }}</span>
            <span v-if="!submitting && !submitted" class="submit-arrow" aria-hidden="true">→</span>
            <span v-else-if="submitting" class="loading-dot" aria-hidden="true"></span>
          </button>

          <p v-if="feedback" class="feedback" :class="{ 'feedback--success': submitted }" role="status">
            {{ feedback }}
          </p>
        </form>

        <div class="support-note">
          <span class="support-note__label">需要帮助？</span>
          <a href="tel:0632-3596666">联系系统管理员</a>
          <span class="support-note__phone">0632-3596666</span>
        </div>

        <p class="copyright">滕州启明眼科医院 · 一切以患者为中心</p>
      </div>
    </main>
  </div>
</template>

<script>
import { isNavigationFailure } from 'vue-router'
import { setEmployee } from '@/store/session'
import { API_BASE } from '@/config/api'

export default {
  name: 'LoginPage',
  data () {
    return {
      account: '',
      password: '',
      remember: false,
      showPassword: false,
      submitting: false,
      submitted: false,
      feedback: '',
      errors: {
        account: '',
        password: ''
      }
    }
  },
  methods: {
    togglePassword () {
      this.showPassword = !this.showPassword
    },
    clearFieldError (field) {
      if (this.errors[field]) {
        this.$set(this.errors, field, '')
      }
      this.feedback = ''
      this.submitted = false
    },
    handleForgotPassword () {
      this.feedback = '请联系系统管理员重置密码'
      this.submitted = false
    },
    validate () {
      this.errors.account = this.account ? '' : '请输入账号'
      this.errors.password = this.password ? '' : '请输入密码'
      return !this.errors.account && !this.errors.password
    },
    async handleSubmit () {
      this.feedback = ''
      this.submitted = false

      if (!this.validate()) {
        return
      }

      this.submitting = true
      try {
        const response = await fetch(`${API_BASE}/api/auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            username: this.account,
            password: this.password
          })
        })
        const result = await response.json()

        if (!response.ok || result.code !== 0) {
          this.feedback = result.message || '账号或密码错误'
          return
        }

        const storage = this.remember ? localStorage : sessionStorage
        const otherStorage = this.remember ? sessionStorage : localStorage
        storage.setItem('qiming.employee', JSON.stringify(result.data))
        otherStorage.removeItem('qiming.employee')
        this.submitting = false
        this.submitted = true
        this.feedback = '登录成功'
        this.$emit('login', result.data)
        setEmployee(result.data)
        await this.redirectAfterLogin()
      } catch (error) {
        this.feedback = '暂时无法连接登录服务，请稍后再试'
      } finally {
        this.submitting = false
      }
    },
    async redirectAfterLogin () {
      const { redirect } = this.$route.query
      // 只接受站内路径，避免被 query 参数带到外部站点
      const isInternalPath = typeof redirect === 'string' &&
        redirect.startsWith('/') &&
        !redirect.startsWith('//')
      const target = isInternalPath && this.$router.resolve(redirect).route.meta.requiresAuth
        ? redirect
        : { name: 'wechat-followers' }
      try {
        await this.$router.replace(target)
      } catch (error) {
        if (!isNavigationFailure(error)) {
          this.submitted = false
          this.feedback = '页面跳转失败，请重试'
        } else if (this.$route.name === 'login') {
          this.submitted = false
          this.feedback = '登录状态未保持，请重新登录'
        }
      }
    }
  }
}
</script>

<style scoped>
.login-page {
  display: block;
  min-height: 100vh;
  background: #f6f8f7;
}

.form-panel {
  display: grid;
  min-height: 100vh;
  padding: 48px 24px;
  place-items: center;
  background: #f6f8f7;
}

.form-wrap {
  width: min(100%, 408px);
}

.mobile-brand {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  margin-bottom: 58px;
  color: #173b46;
  font-size: 15px;
  font-weight: 700;
}

.mobile-brand p {
  margin: 0;
  color: #239b88;
  font-size: 11px;
  letter-spacing: 2.2px;
}

.form-heading {
  margin-bottom: 38px;
}

.form-heading__eyebrow {
  margin-bottom: 15px;
  color: #239b88;
}

.form-heading h2 {
  margin: 0;
  color: #173b46;
  font-size: 34px;
  font-weight: 700;
  letter-spacing: 0;
}

.form-heading p:last-child {
  margin: 12px 0 0;
  color: #7b9091;
  font-size: 14px;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 26px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.field-group label,
.field-label-row label {
  color: #305259;
  font-size: 13px;
  font-weight: 700;
}

.field-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.field-group input {
  width: 100%;
  height: 52px;
  padding: 0 16px;
  border: 1px solid #d8e2df;
  border-radius: 8px;
  outline: none;
  color: #18333a;
  background: #fff;
  font: inherit;
  font-size: 14px;
  transition: border-color 160ms ease, box-shadow 160ms ease;
}

.field-group input::placeholder {
  color: #a9b8b7;
}

.field-group input:focus {
  border-color: #36aa94;
  box-shadow: 0 0 0 4px rgba(54, 170, 148, 0.12);
}

.field-group input.has-error {
  border-color: #d67b66;
}

.text-button {
  padding: 0;
  border: 0;
  color: #268c7e;
  background: transparent;
  cursor: pointer;
  font: inherit;
  font-size: 12px;
}

.text-button:hover {
  color: #176d63;
  text-decoration: underline;
}

.field-error {
  margin: -2px 0 0;
  color: #bc624d;
  font-size: 12px;
}

.form-options {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: -5px;
}

.remember-option {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  color: #718486;
  cursor: pointer;
  font-size: 12px;
}

.remember-option input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
}

.checkbox {
  position: relative;
  width: 16px;
  height: 16px;
  border: 1px solid #c7d6d2;
  border-radius: 4px;
  background: #fff;
  transition: background 160ms ease, border-color 160ms ease;
}

.remember-option input:checked + .checkbox {
  border-color: #299e88;
  background: #299e88;
}

.remember-option input:checked + .checkbox::after {
  position: absolute;
  top: 1px;
  left: 5px;
  width: 4px;
  height: 8px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  content: '';
  transform: rotate(45deg);
}

.submit-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  height: 54px;
  margin-top: 2px;
  border: 0;
  border-radius: 8px;
  color: #fff;
  background: #237f78;
  box-shadow: 0 10px 18px rgba(35, 127, 120, 0.18);
  cursor: pointer;
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  transition: background 160ms ease, transform 160ms ease, box-shadow 160ms ease;
}

.submit-button:hover:not(:disabled) {
  background: #1b6e68;
  box-shadow: 0 12px 22px rgba(35, 127, 120, 0.26);
  transform: translateY(-1px);
}

.submit-button:disabled {
  cursor: wait;
  opacity: 0.78;
}

.submit-arrow {
  font-size: 20px;
  font-weight: 400;
  line-height: 1;
}

.loading-dot {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.42);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 700ms linear infinite;
}

.feedback {
  margin: -10px 0 0;
  color: #bc624d;
  font-size: 12px;
  text-align: center;
}

.feedback--success {
  color: #24856f;
}

.support-note {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  margin-top: 56px;
  color: #8a9b9b;
  font-size: 12px;
}

.support-note a {
  color: #358f84;
  font-weight: 600;
  text-decoration: none;
}

.support-note a:hover {
  text-decoration: underline;
}

.support-note__phone {
  color: #a2afae;
}

.copyright {
  margin: 17px 0 0;
  color: #b0bbba;
  font-size: 11px;
  text-align: center;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 640px) {
  .form-panel {
    display: block;
    min-height: 100vh;
    padding: 28px 22px 30px;
  }

  .form-wrap {
    width: min(100%, 420px);
    margin: 0 auto;
  }

  .mobile-brand {
    margin-bottom: 54px;
    align-items: flex-start;
  }

  .support-note {
    flex-wrap: wrap;
    margin-top: 46px;
  }
}

@media (max-width: 360px) {
  .form-panel {
    padding-right: 18px;
    padding-left: 18px;
  }

  .mobile-brand {
    margin-bottom: 42px;
  }

  .form-options {
    align-items: flex-start;
    gap: 12px;
  }
}
</style>
